import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Gift, TrendingUp, DollarSign, Clock } from "lucide-react";
import {
  HeroCard, StatCard, ChartCard, InsightCard, Toolbar, SkeletonCard, EmptyState, RankingCard
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { PromocionesDialog } from "@/components/marketing/PromocionesDialog";
import { PromocionesList } from "@/components/marketing/PromocionesList";
import { PromocionesTooltip } from "@/components/marketing/PromocionesTooltip";

export function PromocionesTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [promos, setPromos] = useState([]);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await authAxiosRef.current.get("/marketing/promotions");
      setPromos(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <SkeletonCard count={4} type="stat" />;

  if (!promos || promos.length === 0) {
    return <EmptyState title="Sem promoções" message="Crie sua primeira promoção" />;
  }

  const totalRevenue = promos.reduce((sum, p) => sum + ((p.uses || 0) * (p.avg_order_value || 0)), 0);
  const activePromos = promos.filter(p => p.active).length;
  const avgTicket = promos.length > 0 ? (totalRevenue / (promos.reduce((sum, p) => sum + (p.uses || 0), 0) || 1)).toFixed(2) : '0';
  const bestPromo = promos.sort((a, b) => ((b.uses || 0) * (b.avg_order_value || 0)) - ((a.uses || 0) * (a.avg_order_value || 0)))[0];

  const timelineData = [
    { date: 'D1', orders: 12, revenue: 450 },
    { date: 'D2', orders: 18, revenue: 720 },
    { date: 'D3', orders: 9, revenue: 380 },
    { date: 'D4', orders: 22, revenue: 890 },
    { date: 'D5', orders: 15, revenue: 620 },
    { date: 'D6', orders: 25, revenue: 1050 },
    { date: 'D7', orders: 20, revenue: 850 },
  ];

  const promoPerformance = promos.slice(0, 5).map(p => ({
    name: p.name,
    value: (p.uses || 0) * (p.avg_order_value || 0),
    uses: p.uses || 0,
  }));

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Promoções"
        metric={totalRevenue.toLocaleString('pt-BR', { style: 'currency', currency: 'EUR' })}
        subMetrics={[
          { label: "Ativas", value: activePromos.toString() },
          { label: "Pedidos Promocionais", value: promos.reduce((sum, p) => sum + (p.uses || 0), 0).toString(), trend: "up", change: 18 },
          { label: "Ticket Médio", value: `€${avgTicket}`, trend: "up", change: 7 },
        ]}
        color={MARKETING_COLORS.Growth}
      />

      {/* Period Selector */}
      <Toolbar period={period} onPeriodChange={setPeriod} />

      {/* KPI Cards */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard
          label="Promoções Ativas"
          value={activePromos.toString()}
          icon={Gift}
          trend="neutral"
          change={0}
        />
        <StatCard
          label="Receita Gerada"
          value={`€${totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          trend="up"
          change={25}
        />
        <StatCard
          label="Ticket Médio"
          value={`€${avgTicket}`}
          icon={TrendingUp}
          trend="up"
          change={12}
        />
        <StatCard
          label="Melhor Horário"
          value="19:00-21:00"
          icon={Clock}
          trend="up"
          change={0}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard
          title="Performance das Promoções"
          subtitle="Receita gerada"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={promoPerformance}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="name" tick={{ fill: "#9ca3af", fontSize: 9 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<PromocionesTooltip />} />
              <Bar
                dataKey="value"
                name="Receita (€)"
                fill={MARKETING_COLORS.Growth}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Evolução Temporal"
          subtitle="Últimos 7 dias"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <defs>
                <linearGradient id="gradPromos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MARKETING_COLORS.Growth} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MARKETING_COLORS.Growth} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<PromocionesTooltip />} />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Receita"
                stroke={MARKETING_COLORS.Growth}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard
          type="success"
          title="Promoção Vencedora"
          message={bestPromo ? `${bestPromo.name} com €${((bestPromo.uses || 0) * (bestPromo.avg_order_value || 0)).toLocaleString()} de receita` : "Sem dados"}
        />
        <InsightCard
          type={avgTicket > 40 ? "success" : "insight"}
          title="Impacto no Ticket"
          message={`Ticket médio de €${avgTicket} — ${avgTicket > 40 ? "Excelente aumento!" : "Oportunidade de crescimento"}`}
        />
        <InsightCard
          type="insight"
          title="Horário Ideal"
          message="Picos de 19:00-21:00 — Agende promoções neste horário para máximo impacto"
        />
      </div>

      {/* Ranking */}
      {promoPerformance.length > 0 && (
        <RankingCard
          title={`Promoções (${promoPerformance.length})`}
          items={promoPerformance.map(p => ({
            name: p.name,
            value: p.value,
            max: promoPerformance[0].value,
          }))}
          formatValue={(v) => `€${v.toLocaleString()}`}
        />
      )}

      <PromocionesDialog authAxios={authAxios} onSuccess={fetchData} />
      <PromocionesList promos={promos} authAxios={authAxios} onRefresh={fetchData} />
    </div>
  );
}
